/**
 * @description 工具函数
 * @module utils
 */
import type { RepoItemResData, TransferParamsOption, AuthStateInfo } from '@/utils/types';
import type { AxiosResponse } from 'axios';
import { SITE_HOST, AUTH_WINDOW } from '@/constant';
import { CLICK_TIME_MODULE, AVATAR_VISITED_DATE, AVATAR_VISITED_USERNAME, IMAGE_CDN_PREFIX } from '@/constant/index';
import cutParamsInUrl from './cutParamsInUrl';
import generateHash from './generateHash';
import aDocConverter from './aDocConverter';
import { saveLastTimeRepoId, getLastTimeRepoId } from './cookieHandle';
import { formatTime } from './formatTime';
import { xssPurify, highlightWords } from './formatDoc';
import { formatIssuePutData, removeEmptyValue } from './formatData';
import formatTimeDifference from './formatTimeDifference';
import { messageError } from './message';
import { base64ToUtf8, utf8ToBase64 } from './code-transfer';
import type { Topic } from '@/api/home/types';
import { nextTick } from 'vue';
import { VITE_HOST } from '@/utils/tools';
import cookie from 'js-cookie';
import { TOPIC_TYPE } from '@/constant/enum';

const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN;

export const needShowImg = (filePath: string) => {
  const rules = /^.*\.(jpg|jpeg|png|gif|bmp|webp)$/i;
  return rules.test(filePath);
};

/**
 * 返回 axios 的 data, 或者 obj 最内层的 data
 * @returns {object}
 */
export const escapeResData = (obj: AxiosResponse): object => {
  if (!obj) return obj;
  if (!Object.prototype.hasOwnProperty.call(obj, 'data')) return obj;
  if (obj?.headers?.constructor?.name === 'AxiosHeaders') {
    return obj?.data;
  }
  return escapeResData(obj?.data);
};

/**
 * Parse the time to string
 * @param {(Object|string|number)} time
 * @param {string} cFormat
 * @returns {string | null}
 */
function _parseTime(time: string | number | Date, cFormat?: string) {
  if (!time) {
    return null;
  }
  const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}';
  let date;
  if (typeof time === 'object') {
    date = time;
  } else {
    if (typeof time === 'string') {
      if (/^[0-9]+$/.test(time)) {
        time = parseInt(time);
      } else {
        // support safari
        time = time.replace(new RegExp(/-/gm), '/');
      }
    }

    if (typeof time === 'number' && time.toString().length === 10) {
      time = time * 1000;
    }
    date = new Date(time);
  }
  const formatObj = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay()
  };
  const time_str = format.replace(/{([ymdhisa])+}/g, (result, key) => {
    const value = formatObj[key];
    // Note: getDay() returns 0 on Sunday
    if (key === 'a') {
      return ['日', '一', '二', '三', '四', '五', '六'][value];
    }
    return value.toString().padStart(2, '0');
  });
  return time_str;
}

export function fileToBlob(file: any) {
  // 创建 FileReader 对象
  const reader = new FileReader();
  return new Promise((resolve) => {
    // FileReader 添加 load 事件
    reader.addEventListener('load', (e) => {
      let blob;
      if (typeof e.target.result === 'object') {
        blob = new Blob([e.target.result]);
      } else {
        blob = e.target.result;
      }

      resolve(blob);
    });
    // FileReader 以 ArrayBuffer 格式 读取 File 对象中数据
    reader.readAsArrayBuffer(file);
  });
}

/** 图片转 base64 */
export function imageToBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

/** 提取文件地址 */
export function getFilePath(hash: string, filetype: string): string {
  const chars: string[] = hash.split('');
  const sb: string[] = [];
  for (const c of chars) {
    if (isNaN(Number(c))) {
      sb.push(c);
    }
  }
  return `${sb.slice(0, 2).join('')}/${sb.slice(2, 4).join('')}/${hash}.${filetype}`;
}

/** 组合出图片资源地址 */
export function getImageUrl(iam_id?: string, fileType: string = 'png', isAvatar: boolean = true) {
  if (!iam_id) {
    const accountInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    iam_id = accountInfo.iam_id;
  }
  if (isAvatar) {
    const host = 'https://gitcode-img.obs.cn-south-1.myhuaweicloud.com:443/';
    const salt = 'avatar';
    const hash = generateHash(iam_id || '', salt);
    const path = getFilePath(hash, fileType || 'png');
    return host + path + '?time=' + new Date().getTime();
  }
}

/**
 * 提取展示的姓名
 */
export const pickNickName = (author?: any): string => {
  return (
    author?.nick_name ||
    author?.nickName ||
    author?.nickname ||
    author?.name_cn ||
    author?.name ||
    author?.username ||
    ''
  );
};

export { formatUserAliasAtUsername } from './formatUserDisplay';

/**
 * 替换对象中的null为 undefined
 */
export function replaceNull(obj: { [name: string]: any }): any {
  for (const k in obj) {
    if (obj[k] === null) {
      obj[k] = undefined;
    }
  }
  return obj;
}

/**
 * 处理多选数据 1.空值 清空select 2.重复项取消 3.未选中的添加
 */
export function formatSelectedData(
  current: string | object | undefined | null,
  selectedList: any[],
  judge: (a: { [name: string]: unknown }, b: { [name: string]: unknown } | unknown) => boolean
): object[] {
  if (!current) return [];
  const index = selectedList.findIndex((item) => {
    if (typeof item === 'string') {
      return item === current;
    } else {
      return judge && judge(item, current);
    }
  });
  if (index > -1) {
    selectedList.splice(index, 1);
  } else {
    selectedList.push(current);
  }
  return selectedList;
}

/**
 * 处理引用回复文本
 */
export const formatQuoteReply = (str: string) => {
  str = str.trim();
  if (!str) return '';
  str =
    str
      .split('\n')
      .map((e) => '>' + e)
      .join('\n') + '\n\n';
  return str;
};

/**
 * 引用回复时，将作者 username 塞入评论填充中
 * @param authorUsername
 * @returns
 */
export const formatAtAuthor = (authorUsername?: string) => {
  return authorUsername ? `@${authorUsername} \n\n` : '';
};

/**
 * 判断父级元素有没有类名
 */
export function hasClassInParent(target: Element, classNameList: string[]): boolean {
  if (target?.parentElement) {
    if (classNameList.every((name) => target?.classList?.contains(name))) {
      return true;
    } else {
      return hasClassInParent(target?.parentElement, classNameList);
    }
  } else {
    return target ? classNameList.every((name) => target?.classList?.contains(name)) : false;
  }
}

/**
 * 优先展示已经选择的项，已勾选的排在前面
 */
export const sortList = <T extends { value: any }>(list: T[] = [], select: T[] = []): T[] => {
  list = list.slice(0);
  select = select.slice(0);
  if (select[0]) {
    const sList: T[] = [];
    for (let i = 0; i < select.length; i++) {
      const index = list.findIndex((e) => e.value === select[i]?.value);
      if (index > -1) {
        const one = list.splice(index, 1)[0];
        sList.push(one);
      }
    }
    return sList.concat(list);
  } else {
    return list;
  }
};

/**
 * 处理repo数据便于使用repoItem组件
 */
export const repoDataHandler = (data: RepoItemResData[]) => {
  return data
    .filter((item) => {
      return !!item && (item.id || item.resource_id);
    })
    .map((item) => {
      const langs = (item.main_repository_language || []).filter((lang) => {
        return !!lang;
      });
      return {
        id: item.id || item.resource_id || '',
        imgSrc: '',
        title: item.name || '-',
        desc: item.description || '-',
        isStar: item.starred || false,
        tag: item.visibility || '',
        to: `/${item.namespace}`,
        web_url: item.web_url,
        iconHandleList: [
          {
            icon: 'icon-dot-status',
            value: langs.join(',') || '-',
            type: 'language',
            iconColor: 'red',
            label: '',
            to: ''
          },
          { icon: 'gt-star', value: item.star_count, label: '', to: '' },
          { icon: 'gt-fork', value: item.forks_count, label: '', to: '' },
          { icon: 'gt-date', value: item.last_activity_at, label: '', to: '' }
        ]
      };
    });
};

/* 过滤对象中的空属性值
 * @param obj
 * @returns
 */
export function filterEmptyObj(obj: { [name: string]: unknown }) {
  const newObj = obj;
  for (const key in newObj) {
    // 删除空属性值
    if (Object.prototype.hasOwnProperty.call(newObj, key)) {
      if (!newObj[key]) {
        delete newObj[key];
      }
    }
  }
  return newObj;
}

/**
 * 过滤 API 参数对象中的无效值（undefined、null、空字符串）
 * 常用于接口请求前，去除不需要传递的空参数，避免后端接收到无意义字段
 *
 * @param obj 需要过滤的参数对象
 * @returns 过滤后的新对象（不会修改原对象）
 */
export function filterApiParams<T extends Record<string, any>>(obj: T): Partial<T> {
  // 浅拷贝一份对象，避免直接修改原对象
  const result = { ...obj };
  Object.keys(result).forEach((key) => {
    const value = result[key];
    // 如果值为 undefined、null 或空字符串，则删除该属性
    if (value === undefined || value === null || value === '') {
      delete result[key];
    }
  });
  return result;
}

/**
 * 让元素进入全屏
 * @param {string} id 元素id
 *
 */
export const fullscreen = (id: string): void => {
  // @ts-ignore
  if (document.fullscreenElement || document.webkitCurrentFullScreenElement) {
    if (document.exitFullscreen) {
      document.exitFullscreen();
      // @ts-ignore
    } else if (document.mozCancelFullScreen) {
      // @ts-ignore 兼容Firefox
      document.mozCancelFullScreen();
      // @ts-ignore
    } else if (document.webkitExitFullscreen) {
      // @ts-ignore 兼容Chrome, Safari and Opera等
      document.webkitExitFullscreen();
      // @ts-ignore
    } else if (document.msExitFullscreen) {
      // @ts-ignore 兼容IE/Edge
      document.msExitFullscreen();
    }
  } else {
    const dom = document.getElementById(id);
    if (dom?.requestFullscreen) {
      dom.requestFullscreen();
      // @ts-ignore
    } else if (dom?.mozRequestFullScreen) {
      // @ts-ignore 兼容Firefox
      dom.mozRequestFullScreen();
      // @ts-ignore
    } else if (dom?.webkitRequestFullScreen) {
      // @ts-ignore 兼容Chrome, Safari and Opera等
      dom.webkitRequestFullScreen();
      // @ts-ignore
    } else if (dom?.msRequestFullscreen) {
      // @ts-ignore 兼容IE/Edge
      dom.msRequestFullscreen();
    }
  }
};

/**
 * 对象深层递归合并
 */
export const deepMerge = function (target: { [name: string]: unknown }, source: { [name: string]: unknown }) {
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      if (source[key] instanceof Object && key in target && target[key] instanceof Object) {
        deepMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }
  return target;
};
/** 最长公共子序列（模糊匹配） */
export const longestCommonSubsequence = function (str1: string, str2: string) {
  const m = str1.length;
  const n = str2.length;
  const dp = new Array(m + 1);
  for (let i = 0; i <= m; i++) {
    dp[i] = new Array(n + 1).fill(0);
  }
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  let lcs = '';
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (str1[i - 1] === str2[j - 1]) {
      lcs = str1[i - 1] + lcs;
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }
  return lcs;
};
export const blurMatch = function (target: string, keywords: string) {
  const m = target.length;
  const n = keywords.length;
  const dp = new Array(m + 1);
  for (let i = 0; i <= m; i++) {
    dp[i] = new Array(n + 1).fill('');
  }
  const match = [];
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (target[i - 1] === keywords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + keywords[j - 1];
        if (typeof match[j - 1] === 'undefined') match[j - 1] = i - 1;
      } else {
        dp[i][j] = dp[i - 1][j].length > dp[i][j - 1].length ? dp[i - 1][j] : dp[i][j - 1];
      }
    }
  }
  return match;
};

/** 判断是不是首次进入页面 */
export const firstEnter = () => {
  const navigationEntries = performance.getEntriesByType('navigation');
  if (navigationEntries.length > 0) {
    const navEntry = navigationEntries[0];
    return navEntry.type === 'navigate'; // 首次进入
  }
  return false;
};

export const savePageRef = (fullPath: string, name?: string) => {
  let ref = '';
  const currentUrl = window.location.pathname;
  // 上报ref设置
  const BASE_URL = VITE_HOST();
  const beforeRef = sessionStorage.getItem('ref');
  // 如果是从csdn 第三方授权到/oauth/callback 页面后，ref 取sign_path 的值
  if (document.referrer.startsWith('https://passport.csdn.net') && currentUrl === '/oauth/callback') {
    // cookie sign_path，同站的页面登入后进入主站auth callback
    ref = localStorage.getItem('sign_path') || cookie.get('sign_path') || window.page_ref || document.referrer;
    cookie.set('sign_path', '', { expires: -1, domain: cookieDomain });
  } else if (!document.referrer.startsWith(BASE_URL) && firstEnter() && !name) {
    // 如果是从第三方链接收跳转到我们gitcode.com 的页面后，ref取上一个页面 document.referrer 的值
    ref = document.referrer;
  } else {
    // spa本项目之间的路由跳转
    ref = beforeRef || document.referrer || '';
  }
  window.page_ref = ref;
  sessionStorage.setItem('ref', BASE_URL + fullPath);
};

/**
 * 获取注册来源utm_source(有效时间一小时)
 * @returns {string} utm_source
 */
export const getSignUtmSource = () => {
  if (!localStorage) {
    console.error('not support localStorage');
    return;
  }
  const utm_source_sign = localStorage.getItem('utm_source_sign');
  if (utm_source_sign) {
    const intervalTime = 1000 * 60 * 60;
    const currentTime = Date.now();
    const utm_source_sign_time = Number(localStorage.getItem('utm_source_sign_time'));
    if (utm_source_sign_time + intervalTime < currentTime) {
      // 超过一小时，清除utm_source
      localStorage.removeItem('utm_source_sign');
      localStorage.removeItem('utm_source_sign_time');
      return '';
    } else {
      return JSON.parse(utm_source_sign);
    }
  }
  return '';
};

/**
 * @description 移除字符串两边引号
 * @param str string
 * @returns {string}
 */
const removeQuotation = (str: string = '') => str.replace(/['"](.*)['"]/, (match, _p1) => _p1).trim();

/**
 * @description 处理md模板中的配置信息 value
 * @param {val} string
 * @param {type} number 0：处理json和string 1：只处理json 2：只处理string
 * @returns Array|string
 */
const formatMdConfVal = (val: string, type: number = 0) => {
  try {
    if (type === 0) {
      // 处理json和字符串，返回数组
      return /^\[.*]$/.test(val) ? JSON.parse(val) : [removeQuotation(val)];
    } else if (type === 1) {
      return /^\[.*]$/.test(val) ? JSON.parse(val) : [];
    } else if (type === 2) {
      return removeQuotation(val);
    } else {
      return null;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
};
/**
 * 解析md 模板，提取关键内容
 */
export const analyseMdTemplate = (str: string) => {
  const params: { [name: string]: unknown } = {
    assignees: [],
    labels: [],
    title: ''
  };
  str = str.replace(/---\n(.*)\n---\n/s, function (match, p1) {
    try {
      const paramsArr = p1.trim().split('\n');
      paramsArr.forEach((str: string) => {
        const [key]: string[] = str.split(':', 1);
        let val = str.replace(key + ':', '')?.trim() || '';
        if (key.trim() === 'assignees' || key.trim() === 'labels') {
          // assignees  labels 支持 string 和 json
          val = formatMdConfVal(val);
          params[key.trim()] = val;
        } else if (/^\[.*]$/.test(val)) {
          val = formatMdConfVal(val, 1);
          params[key.trim()] = val;
        } else {
          val = formatMdConfVal(val, 2);
          params[key.trim()] = val;
        }
      });
    } catch (error) {
      console.log(error);
    }
    return '';
  });
  return {
    params,
    content: str
  };
};

/**
 * @description: 时间差
 * @param {start, end, format}开始时间, 结束时间, 需要得时间格式
 * 入参传0代表需要格式化最完整得字符串，为2表示模糊到两个单位，为1模糊到一个单位
 * @return {string} 格式化后得时间差例如：'2m30s' '2d3h5m20s'
 */
export function formatterTimeDifference(start: Date | number, end: Date | number, format = 0, pauseTime?: number) {
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();
  pauseTime = pauseTime || 0;
  const diff = endTime - startTime - pauseTime; // 实际运行时间 = 结束时间 - 开始时间 - 暂停时间
  if (diff < 0) {
    return 0;
  }
  return formatTimestamp(diff, format);
}

/**
 * 获取文本是否超出dom宽度
 * @param {el, text, whiteSpace } 要判断得dom元素,或者能获取到元素得class、id；第二个参数可选，要判断的文本；whiteSpace文本换行属性 对比高度时需要换行
 * @returns {boolean} 文本是否超出dom
 */
export function getIsTextOverSize(el: Element | string, text?: string, whiteSpace?: string) {
  const elDom = typeof el === 'string' ? (document.querySelector(el) as Element) : el;
  if (!elDom) return false;

  const containerHeight = elDom.clientHeight;
  const containerWidth = elDom.clientWidth;

  // 创建一个临时元素
  const tempElement = document.createElement('span');
  tempElement.style.visibility = 'hidden';
  tempElement.style.position = 'absolute';
  tempElement.style.whiteSpace = whiteSpace || 'nowrap';
  tempElement.style.wordBreak = 'break-all';

  const style = window.getComputedStyle(elDom);
  tempElement.style.width = containerWidth + 'px';
  tempElement.style.fontSize = style.fontSize;
  tempElement.style.fontFamily = style.fontFamily;
  tempElement.style.lineHeight = style.lineHeight;
  tempElement.style.letterSpacing = style.letterSpacing;
  tempElement.style.fontWeight = style.fontWeight;
  tempElement.style.padding = style.padding;

  tempElement.textContent = text || elDom.textContent;

  // 将临时元素添加到文档中
  document.body.appendChild(tempElement);
  const textWidth = tempElement.scrollWidth;
  const textHeight = tempElement.scrollHeight;

  // 移除临时元素
  document.body.removeChild(tempElement);

  // 对于单行文本（nowrap），比较宽度；对于多行文本，比较高度
  // 添加1px的容差来处理浮点数精度问题
  if (whiteSpace === 'nowrap') {
    return textWidth > containerWidth + 1;
  } else {
    return text ? textWidth > containerWidth + 1 : textHeight > containerHeight + 1;
  }
}

/** 数字千分加, */
export const numPretty = (num: number) => {
  const arr = [];
  let str = '';
  let index = 0;
  let sign = false;
  if (num < 0) {
    num = -num;
    sign = true;
  }
  while (num) {
    str = (num % 10) + str;
    index++;
    if (index === 3) {
      arr.unshift(str);
      str = '';
      index = 0;
    }
    num = Math.floor(num / 10);
  }
  if (str) arr.unshift(str);
  return `${sign ? '-' : ''}${arr.join(',') || '0'}`;
};

/** 生成随机ID */
export const UUID = () => {
  const temp_url = URL.createObjectURL(new Blob());
  const uuid = temp_url.toString(); // blob:https://xxx.com/b250d159-e1b6-4a87-9002-885d90033be3
  URL.revokeObjectURL(temp_url);
  return uuid.slice(uuid.lastIndexOf('/') + 1);
};

function getScrollTop() {
  // 浏览器兼容
  let scrollTop = 0;
  if (document.documentElement && document.documentElement.scrollTop) {
    scrollTop = document.documentElement.scrollTop;
  } else if (document.body) {
    scrollTop = document.body.scrollTop;
  }
  return scrollTop;
}
function getClientHeight() {
  // 浏览器兼容
  let clientHeight = 0;
  if (document.body.clientHeight && document.documentElement.clientHeight) {
    clientHeight = document.body.clientHeight;
  } else {
    clientHeight =
      document.body.clientHeight > document.documentElement.clientHeight
        ? document.body.clientHeight
        : document.documentElement.clientHeight;
  }
  return clientHeight;
}

// 回到顶部
export function scrollToTop() {
  const c = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
  if (c > 0) {
    window.requestAnimationFrame(scrollToTop);
    if (c >= 8) {
      window.scrollTo(0, c - c / 4);
    } else {
      window.scrollTo(0, 0);
    }
  }
}
function getScrollHeight() {
  // 浏览器兼容
  return Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
}

/** 根据锚点滚动到指定位置 */
// md h标签生成id的规则
function generateHeadingId(text: string): string {
  const id = text
    .toLowerCase()
    .trim()
    .replace(/[^\w\u4e00-\u9fa5\- ]/g, '') // 保留中文、字母、数字、连字符和空格
    .replace(/\s+/g, '-'); // 空格替换为连字符
  return id;
}
/*
  增加兼容性，如果找不到则根据md生成h标签id的凡是转换一下
*/
const findAnchorElement = (anchor: string): HTMLElement | null => {
  // 1. 先尝试通过 getElementById 查找(支持数字开头的id)
  const elById = document.getElementById(anchor);
  if (elById) return elById;
  // 2. 再尝试通过 name 属性查找
  const elByName = document.querySelector(`a[name="${anchor}"]`);
  if (elByName) return elByName as HTMLElement;
  // 3. 最后尝试通过生成的heading id查找
  const headingId = generateHeadingId(anchor);
  return document.getElementById(headingId);
};

export const scrollToAnchor = (anchor: string, offsetNum?: number) => {
  const el = findAnchorElement(anchor);
  if (!el) return;
  el.scrollIntoView({ behavior: 'auto' });
  if (offsetNum) {
    if (getScrollTop() + getClientHeight() < getScrollHeight()) {
      window.scrollBy(0, offsetNum);
      return;
    }
    const top = el?.getBoundingClientRect()?.top || 0;
    // 如果需要相对滚动但是又被导航栏遮挡， offsetNum偏移量 大于元素距离顶部距离，则滚动到元素顶部
    if (top <= Math.abs(offsetNum)) {
      window.scrollBy(0, top + offsetNum);
    }
  }
};

// 滚动相关处理
let baseHeadHeight = 0;
let repoHeadHeight = 0;
export const scrollToReadmeAnchor = () => {
  // 顶部 + 项目顶部 间隔 仅获取一次dom
  baseHeadHeight =
    baseHeadHeight || (document.querySelector('.gitcode-header-wrapper') as HTMLElement)?.offsetHeight || 56;
  repoHeadHeight = repoHeadHeight || (document.querySelector('.repo-info') as HTMLElement)?.offsetHeight || 52;
  const offset = -(baseHeadHeight + repoHeadHeight);
  nextTick(() => {
    scrollToAnchor('markdown-card-anchor', offset);
  });
};

/** 生成随机不重复id */
export function genNonDuplicateID() {
  let str = Math.random().toString(36).substring(3);
  str += Date.now().toString(16).substring(4);
  return str;
}

/** 根据环境变量跳转路由 */
export const goHrefPage = (path: string) => {
  window.location.href = `${SITE_HOST}/${path}`;
};

/**
 * 判断给定的项目topic是否属于gstar
 *
 * @param topics 项目topic列表
 * @returns 如果ID在Gstar仓库列表中则返回true，否则返回false
 */
export const isGstarRepo = (topics: any[]) => {
  const gstarTopicIds = import.meta.env.VITE_GSTAR_TOPIC.split(',');
  let isGstar = false;
  topics &&
    topics.forEach((item) => {
      if (gstarTopicIds.includes(item.topic_id || item.id)) {
        isGstar = true;
      }
    });

  return isGstar;
};

/**
 * 判断给定的项目topic是否属于可信开源
 *
 * @param topics 项目topic列表
 * @returns 如果ID在Trusted仓库列表中则返回true，否则返回false
 */
export const isTrustedRepo = (topics: any[]) => {
  const trustedTopicIds = import.meta.env.VITE_TRUSTED_TOPIC.split(',');
  let isTrusted = false;
  topics &&
    topics.forEach((item) => {
      if (trustedTopicIds.includes(item.topic_id || item.id)) {
        isTrusted = true;
      }
    });
  return isTrusted;
};

/**
 * 将某个属性值拷贝挪动到另外一个属性下
 * @param data 原数据
 * @param config 转移配置
 * @return {array} 加工后的数据
 */
export function transferParams<T extends { [name: string]: unknown }>(data: T[], config: TransferParamsOption[]) {
  data = data ?? [];
  data[0] &&
    data.forEach((obj) => {
      config.forEach((option: TransferParamsOption) => {
        // 没有来源key，使用函数处理
        if (typeof option[0] === 'function') {
          obj[option[1]] = option[0](obj);
        } else {
          if (option[2] === undefined) {
            // 没有默认值正常赋值
            obj[option[1]] = obj[option[0]];
          } else {
            // 使用默认值
            obj[option[1]] = option[2];
          }
        }
      });
    });
  return data;
}

/**
 * 获取文件名
 *
 * @param path 文件路径
 * @returns 返回文件名
 */
export const getFileName = (path: string) => {
  return path.lastIndexOf('/') !== -1 ? path.slice(path.lastIndexOf('/') + 1) : path;
};

/**
 * 流水线运行时间的转换,时间为毫秒
 * 入参：timestamp：number 为毫秒
 * 入参：format：number =>
 * 从day,hour,minute,second,4个单位取值拼接，默认值0，拼接全部，大于0，从day开始拼接
 * @returns {number} timestamp
 */
export const formatTimestamp = (timestamp: number, format = 0) => {
  const diffDays = Math.floor(timestamp / (24 * 60 * 60 * 1000));
  const diffHours = Math.floor((timestamp - diffDays * 24 * 60 * 60 * 1000) / (60 * 60 * 1000));
  const diffMinutes = Math.floor(
    (timestamp - diffDays * 24 * 60 * 60 * 1000 - diffHours * 60 * 60 * 1000) / (60 * 1000)
  );
  const diffSeconds = Math.floor(
    (timestamp - diffDays * 24 * 60 * 60 * 1000 - diffHours * 60 * 60 * 1000 - diffMinutes * 60 * 1000) / 1000
  );
  const dayStr = diffDays > 0 ? `${diffDays}d` : '';
  const hoursStr = diffHours > 0 ? `${diffHours}h` : '';
  const minutesStr = diffMinutes > 0 ? `${diffMinutes}m` : '';
  const secondsStr = diffSeconds > 0 ? `${diffSeconds}s` : '';
  const timeArr = [dayStr, hoursStr, minutesStr, secondsStr].filter(Boolean);
  if (format) {
    return timeArr.filter((_, index) => index < format).join('');
  } else {
    return timeArr.join('');
  }
};

/**
 * 组合 username 和 token
 * @returns {string} mirror 地址
 */
export const compositeMirrorUrl = (url: string, username?: string, token?: string) => {
  try {
    let urlObj: any;
    const matchRes = url.match(/git@(.*):(.*)/); // example: git@gitee.com:oldCabbage/test-push-repo1.git
    const [hostname, pathname] = matchRes || [];
    if (!matchRes) {
      urlObj = new URL(url);
    }

    if (username && token) {
      // 有 otken
      if (matchRes) {
        return `https://${username}:${token}@${hostname + '/' + pathname}`;
      } else {
        return `https://${username}:${token}@${urlObj.hostname + urlObj.pathname}`;
      }
    } else {
      if (matchRes) {
        return `https://${hostname}/${pathname}`;
      } else {
        return url;
      }
    }
  } catch (error) {
    console.log(error);
    return url;
  }
};

/** 获取文件在仓库下的路径 */
export const getRelativePath = (filePath: string) => {
  const relativePath = filePath.split('/');
  relativePath.pop();
  return relativePath.length ? `${relativePath.join('/')}/` : '';
};

/** 处理头像红点和记录成长中心点击(后续可扩充) */
export const handleAvatarRedPoint = (type: string, other?: any) => {
  if (type === CLICK_TIME_MODULE.GROWTH_CENTER) {
    // 控制头像上的红点
    const dateString = new Date().toLocaleDateString('zh-CN');
    localStorage.setItem(AVATAR_VISITED_DATE, dateString);
    localStorage.setItem(AVATAR_VISITED_USERNAME, other);
  }
};

const sleep = (time: number) => {
  return new Promise((resolve) => setTimeout(resolve, time));
};

const INCUBATION_TOPICS = import.meta.env.VITE_INCUBATION_TOPIC || '';
/**
 * 从topic判断是否是孵化项目
 */
export const isIncubationRepo = (topics: Topic[] = []) => {
  if (!topics) return false;
  const incubationIds = INCUBATION_TOPICS.split(',');
  if (
    topics.some(
      (e) =>
        (/孵化项目/.test(e.name) || incubationIds.includes(e.topic_id || e.id)) && e.type === TOPIC_TYPE.OPERATOR_TOPIC
    )
  ) {
    return true;
  }
  return false;
};

/**
 * 是否为fork仓库
 * @returns boolean
 */
export const isForkRepo = (repoInfo: any) => {
  return !!repoInfo?.forked_from_project;
};

export const getCNDImageUrl = (path: string): string => {
  return `${IMAGE_CDN_PREFIX}${path}`;
};

// 禁止链接跳转的分支规则
export function branchLinkIsDisabled(branch: string) {
  return branch?.startsWith('refs/');
}

// 是否为轻量级pr
export function prIsLite(branch: string) {
  return branch.startsWith('refs/merge-requests');
}

/**
 * 清除选中的文本
 */
export const clearSelection = () => {
  if (window.getSelection) {
    window.getSelection()?.removeAllRanges();
  } else if (document.selection) {
    document.selection?.empty();
  }
};

// 判断是否为苹果电脑
export const isMac = () => {
  const userAgent = navigator.userAgent;
  return userAgent.includes('Macintosh');
};

/**
 * 是否为第三方授权页子窗口
 */
export const isAuthWindow = () => window.name === AUTH_WINDOW;

/**
 * 计算子窗口特性，保持居中
 * @param {number} width 窗口宽度
 * @param {number} height 窗口高度
 * @returns {string} 窗口特性列表
 */
export const computeWindowFeatures = (width: number = 800, height: number = 800) => {
  const _left = window.screenX !== undefined ? window.screenX : window.screenLeft;
  const _top = window.screenY !== undefined ? window.screenY : window.screenTop;
  let screenWidth = window.innerWidth;
  let screenHeight = window.innerHeight;
  screenWidth = screenWidth <= width ? width : screenWidth;
  screenHeight = screenHeight <= height ? height : screenHeight;
  const left = Math.floor(screenWidth / 2 - width / 2);
  const top = Math.floor(screenHeight / 2 - height / 2);
  const windowFeatures = `width=${width}, height=${height}, left=${_left + left}, top=${_top + top}`;
  return windowFeatures;
};

/**
 * 提取校验码和校验码来源平台的标识
 * @example state=f9f95d16d1a1021f3e6b751d1ebcd299；state=f9f95d16d1a1021f3e6b751d1ebcd299_getcsdnpassportuv
 * @param {string} state 校验码（路由query参数）
 */
function extractAuthStateInfo(state: string = ''): AuthStateInfo {
  const excRes = /(.*)_(.*)/.exec(state as string);
  if (excRes) {
    return { state: excRes[1], stateFrom: excRes[2] };
  } else {
    return { state, stateFrom: '' };
  }
}

// commit用户跳转路径(若不存在，应禁止跳转)
export const getCommitUserPath = (commit: any) => {
  // return commit?.committer_user_name || commit?.author_user_name;
  return commit?.committer_user_name;
};

import { decodeMimeWords } from 'lettercoder';
// commit显示的用户名
export const getCommitUserName = (commit: any) => {
  // return commit?.committer_user_name || commit?.author_user_name || commit?.committer_name || commit?.author_name || '-';
  const name = commit?.committer_user_name || commit?.committer_name || '-';
  if (name.indexOf('UTF-8') > -1) {
    return decodeMimeWords(name);
  }
  return name;
};

/**
 * 移除Url中拼接的isLogin参数
 * @example
 * @param {string} url http 地址
 */
export function removeIsLoginParam(url: string) {
  const isAbsolute = url.startsWith('http');
  const fullUrl = isAbsolute ? url : `${location.origin}${url}`; // 相对路径拼接域名以便解析
  const urlObj = new URL(fullUrl);
  // 删除 'isLogin' 参数
  urlObj.searchParams.delete('isLogin');
  // 返回移除参数后的 URL
  return urlObj.toString();
}

// 解析
export function extractRepoInfoWithURL(repoUrl: string) {
  if (!repoUrl) return '';
  const url = new URL(repoUrl);
  const pathname = url.pathname.replace(/^\//, '');
  const parts = pathname.replace(/.git$/, '').split('/').filter(Boolean);
  return parts.join('/');
}

// 千分号K百万分号M单位

// 此处可以增加单位定义
const DECIMAL_PACE = [
  { unit: 'K', base: 1e3 },
  { unit: 'M', base: 1e6 } // 百万
];

// 大于100，转换成 xxx k
const DECIMAL_PACE2 = [{ unit: 'K', base: 1e3 }];

const BYTE_PACE = [
  { unit: 'B', base: 1 },
  { unit: 'KB', base: Math.pow(2, 10) },
  { unit: 'MB', base: Math.pow(2, 20) },
  { unit: 'GB', base: Math.pow(2, 30) }
];

export const transferNumber = ({
  number,
  fixed = 2,
  byte,
  gt100
}: {
  number: number | string;
  fixed?: number;
  byte?: boolean;
  gt100?: boolean;
}): string => {
  try {
    const pace = byte ? BYTE_PACE : gt100 ? DECIMAL_PACE2 : DECIMAL_PACE;
    return pace.reduce((res, { base, unit }): string => {
      const _base = gt100 && !byte ? 100 : base;
      if ((number as number) >= _base) {
        const fixedBase = Math.pow(10, fixed); // 保留的小数位个数，修正四舍五入
        res = `${parseFloat((Math.round(((number as number) * fixedBase) / base) / fixedBase).toFixed(fixed))} ${unit}`;
      }
      return res;
    }, `${number}`);
  } catch (err) {
    // ignore
  }

  return `${number}`;
};

/**
 * 直接从 window.location.href 解析 query 参数
 */
export const getQueryParams = (): Record<string, string> => {
  const url = new URL(window.location.href);
  const params: Record<string, string> = {};
  url.searchParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
};

export const isOpenHarmonyDocsRepo = () => {
  const repoList = ['/openharmony/docs', '/yinlin/docs'];
  return repoList.some((prefix) => window.location.pathname.startsWith(prefix));
};

/**
 * 判断 PR 是否为草稿状态
 * @param title - PR 标题
 * @param state - PR 状态（可选）
 * @returns 如果状态为 opened 且标题以 [WIP] 开头，返回 'openedWip'，否则返回 state
 */
export const isPrDraft = ({ title, state }: { title: string; state?: string }): string | undefined => {
  if (state === 'opened' && title.startsWith('[WIP]')) {
    return 'openedWip';
  }
  return state;
};

export {
  cutParamsInUrl,
  generateHash,
  aDocConverter,
  xssPurify,
  highlightWords,
  saveLastTimeRepoId,
  getLastTimeRepoId,
  formatTime,
  formatTimeDifference,
  messageError,
  formatIssuePutData,
  removeEmptyValue,
  base64ToUtf8,
  utf8ToBase64,
  sleep,
  extractAuthStateInfo
};
